import { Fragment, MouseEvent, useMemo, useState } from 'react';
import classNames from 'classnames';

import { pluralize } from '@/lib/utils/string';
import { useTrackEvent } from '@/lib/analytics';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { Button, IconButton } from '@/components/button';
import { createBackNavigationToast, useToastContext } from '@/components/toast';
import { DropdownIconSizePx } from '@/components/dropdown';

import { isBasePipeline, isPipelineParentOf } from '@/core/pipeline';
import { isCellWithPipeline } from '@/core/cell';

import { useAccountTimezone } from '@/lib/accounts/context';

import { getExplorationCellIndex } from '@/core/exploration';

import type {
  DeriveFieldOperation,
  Exploration,
  Fields,
  FilterOperation,
  JoinPipelineOperation,
  Pipeline,
  PipelineOperation,
  RelationAggregateOperation,
} from '../types';
import {
  removeOperation,
  updateOperation,
  replaceOperations,
  addOperation,
  updateCellPipeline,
  moveOperation,
  addOperations,
} from './utils';
import {
  getPipelineStateAtIndexOrThrow,
  getFinalStateOrThrow,
  getFieldsWithRelatedFields,
} from '../pipeline/state';
import {
  getOperationIcon,
  getOperationTitle,
  getOperationOverview,
} from '../pipeline/format-operation';
import { getVariableDefinitions, restoreInvalidOperation } from '../utils';
import { sortFields } from '../utils/field-ordering';
import {
  formatCellInstances,
  getJoinedPipelineTitle,
  isJoinedPipeline,
  pipelineHeaderColor,
  toTitleCase,
} from './utils/format';
import { EditOperation } from './edit-operation';
import { SortableItemSimple } from '../components/sortable-item';
import { Separator } from './separator';
import { SavedPipelineHeader } from './saved-pipeline-header';
import { PipelineHeader } from './pipeline-header';
import { SavedOperation } from './saved-operation';
import { Operation } from './operation';
import {
  countOperations,
  dereferencePipeline,
  ensureFakeBaseModelExists,
  flattenFieldGroups,
  flattenPipeline,
  getChildPipelineCells,
  getParentPipelineColor,
  getParentPipelineTitle,
  getPipelineColor,
} from '../pipeline/utils';
import { useExplorationContext } from '../exploration/exploration-context';
import { FormId, useExplorationUIContext } from '../exploration/exploration-ui-context';
import { CombineEditor } from '../exploration/combine';
import { AddOperation } from './add-operation';
import { useMetadataContext } from '../metadata-context';
import { getModelOrThrow } from '../model/utils';
import { isValidOperation } from '../pipeline/operation';
import { BasePipelineSearchModal } from '../exploration-search/base-pipeline-search-modal';
import { useExplorationCellContext } from '../exploration/exploration-cell-context';

import styles from './pipeline.module.scss';

interface EditPipelineProps {
  title: string;
  pipeline: Pipeline;
  setExploration: (exploration: Exploration) => void;
  recordType: Fields;
  grouping: string[];
  onCombine: () => void;
}

export const formatModelLabels = (labels: Record<string, string | undefined>): React.ReactNode => {
  const labelsWithValues = Object.keys(labels).filter((key) => labels[key] !== undefined);
  if (labelsWithValues.length === 0) {
    return null;
  }
  return (
    <div>
      {labelsWithValues.map((key) => (
        <div key={key}>
          <strong>{toTitleCase(key)}</strong>: {labels[key]}
        </div>
      ))}
    </div>
  );
};

export const EditPipeline = (props: EditPipelineProps) => {
  const { pipeline } = props;

  const { metrics, models } = useMetadataContext();
  const timezone = useAccountTimezone();

  const { exploration, getVariables } = useExplorationContext();
  const {
    addFormData,
    editFormIndex,
    openAddMenu,
    openAddForm,
    closeAddForm,
    openEditForm,
    closeEditForm,
    selectedCell,
  } = useExplorationUIContext();
  const selectedCellIndex =
    selectedCell !== null ? getExplorationCellIndex(exploration, selectedCell.id) : null;
  const { getCellViewOption } = useExplorationCellContext();

  const [isPipelineSearchOpen, setPipelineSearchVisibility] = useState(false);

  const variables = getVariables();
  const variableDefinitions = useMemo(() => getVariableDefinitions(exploration), [exploration]);

  const flattenedPipeline = flattenPipeline(pipeline, exploration);
  const stateContext = {
    models: ensureFakeBaseModelExists(models, flattenedPipeline),
    variables,
    metrics,
  };

  const parentPipeline = isBasePipeline(pipeline)
    ? null
    : {
        ...flattenedPipeline,
        operations: flattenedPipeline.operations.slice(
          0,
          flattenedPipeline.operations.length - pipeline.operations.length,
        ),
      };
  const dereferencedPipeline = dereferencePipeline(flattenedPipeline, exploration);
  const baseModel = getModelOrThrow(stateContext.models, dereferencedPipeline.baseModelId);
  const title = getParentPipelineTitle(exploration, pipeline) ?? props.title;

  // Dragged index is needed to which element from pipeline is being dragged. This is also stored in drag event
  // but due to browser security it is not readable until drop event. We need to know the index during dragEnter
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isParentCollapsed, setIsParentCollapsed] = useState(true);

  if (selectedCell === null || selectedCellIndex === null) {
    throw new Error('EditPipeline requires a selected cell');
  }
  const addToast = useToastContext();

  const trackEvent = useTrackEvent();

  const setMainPipelineOperation = (operation: PipelineOperation, atIndex: number) => {
    const updatedExploration = updateOperation(
      exploration,
      selectedCell.id,
      atIndex,
      operation,
      stateContext,
    );
    warnIfOperationsRemoved(updatedExploration);

    props.setExploration(updatedExploration);
    closeEditForm();
    trackEvent('Explore Pipeline Updated', { title, pipeline, operation, atIndex });
  };

  const setParentPipelineOperation = (operation: PipelineOperation, atIndex: number) => {
    const updatedExploration = updateOperation(
      updateCellPipeline(exploration, selectedCell.id, flattenedPipeline, stateContext),
      selectedCell.id,
      atIndex,
      operation,
      stateContext,
    );
    const removedOperationsCount =
      countOperations(exploration) - countOperations(updatedExploration);

    props.setExploration(updatedExploration);
    closeEditForm();

    trackEvent('Explore Pipeline Updated', {
      title,
      pipeline: flattenedPipeline,
      operation,
      atIndex,
    });
    if (removedOperationsCount > 0) {
      addToast(
        createBackNavigationToast(
          'Pipeline Step Deleted and Pipeline Detached from Parent',
          'The change caused the pipeline to be detached from the parent and other pipeline step(s) to be deleted. To revert the changes you can',
          'click undo',
        ),
      );
    } else {
      addToast(
        createBackNavigationToast(
          'Pipeline Detached From Parent',
          'The change caused the pipeline to be detached from the parent. To revert the changes',
          'click undo',
        ),
      );
    }
  };

  const handleAddPrefilledRelationAggregateOperation = (
    atIndex: number,
    operation: RelationAggregateOperation,
  ) => {
    const index = atIndex + 1;
    openAddForm({
      index: index > pipeline.operations.length - 1 ? -1 : index,
      formId: 'addRelationAggregate',
      parameters: operation.parameters,
    });

    trackEvent('Explore Pipeline Add Prefilled Relation Aggregate Operation', {
      title,
      pipeline,
      operation,
    });
  };

  const handleAddPrefilledDeriveFieldOperation = (
    atIndex: number,
    operation: DeriveFieldOperation,
  ) => {
    const index = atIndex + 1;
    openAddForm({
      index: index > pipeline.operations.length - 1 ? -1 : index,
      formId: 'addDeriveField',
      parameters: operation.parameters,
    });

    trackEvent('Explore Pipeline Add Prefilled Derive Field Operation', {
      title,
      pipeline,
      operation,
    });
  };

  const handleAddPrefilledFilterOperation = (atIndex: number, operation: FilterOperation) => {
    const index = atIndex + 1;
    openAddForm({
      index: index > pipeline.operations.length - 1 ? -1 : index,
      formId: 'addFilter',
      parameters: operation.parameters,
    });

    trackEvent('Explore Pipeline Add Prefilled Filter Operation', {
      title,
      pipeline,
      operation,
    });
  };

  const handleAddPrefilledOperation = (atIndex: number, operation: PipelineOperation) => {
    switch (operation.operation) {
      case 'filter':
        handleAddPrefilledFilterOperation(atIndex, operation);
        break;
      case 'relationAggregate':
        handleAddPrefilledRelationAggregateOperation(atIndex, operation);
        break;
      case 'deriveField':
        handleAddPrefilledDeriveFieldOperation(atIndex, operation);
        break;
    }
  };

  const handleRemoveOperation = (event: MouseEvent, atIndex: number) => {
    event.stopPropagation();

    const updatedExploration = removeOperation(exploration, selectedCell.id, atIndex, stateContext);
    props.setExploration(updatedExploration);

    trackEvent('Explore Pipeline Operation Removed', {
      title,
      pipeline,
      operation: pipeline.operations[atIndex],
    });
  };

  const handleRemoveParentPipelineOperation = (event: MouseEvent, atIndex: number) => {
    event.stopPropagation();

    const updatedExploration = removeOperation(
      updateCellPipeline(exploration, selectedCell.id, flattenedPipeline, stateContext),
      selectedCell.id,
      atIndex,
      stateContext,
    );
    props.setExploration(updatedExploration);

    trackEvent('Explore Pipeline Operation Removed', {
      title,
      pipeline: flattenedPipeline,
      operation: flattenedPipeline.operations[atIndex],
    });

    addToast(
      createBackNavigationToast(
        'Pipeline Step Deleted',
        'Please notice that pipeline was detached from the parent. To revert the changes you can',
        'click undo',
      ),
    );
  };

  const handleAddOperation = ({
    position,
    operation,
  }: {
    position: number;
    operation: PipelineOperation;
  }) => {
    trackEvent('Explore Pipeline Operation Added', { title, pipeline, operation });
    const updatedExploration = addOperation(
      exploration,
      selectedCell.id,
      position,
      operation,
      stateContext,
    );
    warnIfOperationsRemoved(updatedExploration);

    props.setExploration(updatedExploration);
    closeAddForm();
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    const updatedExploration = moveOperation(
      exploration,
      selectedCellIndex,
      fromIndex,
      toIndex,
      stateContext,
    );
    warnIfOperationsRemoved(updatedExploration);
    props.setExploration(updatedExploration);
  };

  const handleDisableOperation = (event: MouseEvent, atIndex: number, disabled: boolean) => {
    trackEvent('Explore Pipeline Operation Disable Toggled', {
      title,
      pipeline,
      operation: pipeline.operations[atIndex],
    });

    event.stopPropagation();
    const operations = pipeline.operations.map((operation, index) => {
      if (index === atIndex) {
        return { ...operation, disabled: disabled ? true : undefined };
      }
      return operation;
    });
    props.setExploration(replaceOperations(exploration, selectedCell.id, operations, stateContext));
  };

  const handleDisableParentPipelineOperation = (
    event: MouseEvent,
    atIndex: number,
    disabled: boolean,
  ) => {
    event.stopPropagation();

    const updatedExploration = updateCellPipeline(
      exploration,
      selectedCell.id,
      {
        ...flattenedPipeline,
        operations: flattenedPipeline.operations.map((operation, index) => {
          if (index === atIndex) {
            return { ...operation, disabled: disabled ? true : undefined };
          }
          return operation;
        }),
      },
      stateContext,
    );

    trackEvent('Explore Pipeline Saved Operation Disable Toggled', {
      title,
      pipeline,
      operation: flattenedPipeline.operations[atIndex],
    });
    props.setExploration(updatedExploration);

    addToast(
      createBackNavigationToast(
        'Pipeline detached from parent',
        'The change caused the pipeline to be detached from the parent. To revert the changes',
        'click undo',
      ),
    );
  };

  const handleDetachPipeline = () => {
    props.setExploration(
      updateCellPipeline(exploration, selectedCell.id, flattenedPipeline, stateContext),
    );

    trackEvent('Exploration Pipeline Detached', {
      exploration,
      cell: exploration.view.cells[selectedCellIndex],
      cellIndex: selectedCellIndex,
    });
  };

  const warnIfOperationsRemoved = (updatedExploration: Exploration) => {
    const removedOperationsCount =
      countOperations(exploration) - countOperations(updatedExploration);

    if (removedOperationsCount > 0) {
      addToast(
        createBackNavigationToast(
          'Pipeline Step Deleted',
          'The change caused one or more pipeline steps to be deleted. To revert the changes you can',
          'click undo',
        ),
      );
    }
  };

  const firstOperation = pipeline.operations.at(0);
  const shouldRenderHeader =
    firstOperation === undefined ||
    restoreInvalidOperation(firstOperation).operation !== 'joinPipeline';
  const shouldRenderModelStep = flattenedPipeline.operations.at(0)?.operation !== 'sql';

  const parentPipelineColor = shouldRenderHeader
    ? getParentPipelineColor(exploration, pipeline)
    : undefined;

  const instanceCells = getChildPipelineCells(exploration, pipeline);
  const instanceCount = instanceCells.length;
  const pipelineColor = getPipelineColor(exploration, pipeline);

  const childPipelineIds = instanceCells
    .filter(isCellWithPipeline)
    .filter((cell) => isPipelineParentOf(pipeline, cell.pipeline))
    .map((cell) => cell.pipeline.pipelineId);

  const handleBasePipelineChange = (newPipeline: Pipeline) => {
    // Add operations from the current pipeline to the new pipeline
    const updatedExploration = addOperations(
      updateCellPipeline(
        exploration,
        selectedCell.id,
        {
          ...newPipeline,
          pipelineId: pipeline.pipelineId, // Preserve the pipeline ID
        },
        stateContext,
      ),
      selectedCell.id,
      newPipeline.operations.length,
      pipeline.operations,
      stateContext,
    );

    props.setExploration(updatedExploration);
    setPipelineSearchVisibility(false);
  };

  const formattedLabels = formatModelLabels({ ...baseModel.labels, type: undefined }); // "type" would always be "model"
  const showAddOperation = addFormData?.index === -1;
  // Do not show add button if SQL operation has no fields detected yet.
  const showAddButton =
    !showAddOperation &&
    !(firstOperation?.operation === 'sql' && (firstOperation.parameters.fields?.length ?? 0) === 0);
  const isCombineEnabled = flattenedPipeline.operations.at(0)?.operation !== 'sql';

  const pipelineTitleById = useMemo(
    () =>
      new Map(
        exploration.view.cells
          .filter(isCellWithPipeline)
          .map<[string, string]>((cell) => [cell.pipeline.pipelineId, cell.title ?? '']),
      ),
    [exploration],
  );

  return (
    <>
      {instanceCount > 0 && (
        <div
          className={classNames(styles.masterHeader, pipelineHeaderColor(pipelineColor))}
          title={formatCellInstances(instanceCells)}>
          <PipelineHeader title="Master Instance" icon="Parent" description={title} />
          <div className={styles.masterDescription}>
            <Icon name="Instance" size={16} />
            Pipeline used in {instanceCount} {pluralize(instanceCount, 'instance', 'instances')}
          </div>
          <Separator icon="VerticalEquals" size={8} />
        </div>
      )}
      <div className={classNames(styles.pipeline, pipelineHeaderColor(parentPipelineColor))}>
        {shouldRenderHeader && (
          <>
            {parentPipeline !== null ? (
              <div>
                <SavedPipelineHeader
                  title="Instance"
                  icon="Instance"
                  description={title}
                  collapsed={isParentCollapsed}
                  onClick={() => setIsParentCollapsed(!isParentCollapsed)}
                  actionItems={[
                    {
                      label: 'Replace base model',
                      onClick: () => setPipelineSearchVisibility(true),
                      icon: <Icon name="Repeat" size={DropdownIconSizePx} />,
                    },
                    ...(isCombineEnabled
                      ? [
                          {
                            label: 'Combine',
                            onClick: props.onCombine,
                            icon: <Icon name="Combine" size={DropdownIconSizePx} />,
                          },
                        ]
                      : []),
                    {
                      label: 'Detach instance from parent pipeline',
                      onClick: handleDetachPipeline,
                      icon: <Icon name="Detach" size={DropdownIconSizePx} />,
                    },
                  ]}
                />
                {shouldRenderModelStep && (
                  <div
                    className={classNames(styles.collapsibleOperation, {
                      [styles.collapsed]: isParentCollapsed,
                    })}>
                    <SavedOperation
                      icon="Model"
                      title="Data model"
                      description={
                        isJoinedPipeline(flattenedPipeline)
                          ? getJoinedPipelineTitle(
                              flattenedPipeline,
                              flattenedPipeline.operations[0] as JoinPipelineOperation,
                              models,
                              exploration,
                            )
                          : baseModel.name
                      }
                      onClick={() => setPipelineSearchVisibility(true)}
                    />
                  </div>
                )}
                {parentPipeline.operations.map((operation, i) => {
                  if (operation.operation === 'joinPipeline') {
                    return null;
                  }
                  const pipelineState = sortFields(
                    getPipelineStateAtIndexOrThrow(dereferencedPipeline, i, stateContext),
                    props.recordType,
                    props.grouping,
                    getCellViewOption('fieldOrder'),
                  );

                  const editEnabled = operation.operation !== 'sql';
                  const removeEnabled = !isParentCollapsed && operation.operation !== 'sql';

                  return (
                    <div
                      key={i}
                      className={classNames(styles.collapsibleOperation, {
                        [styles.collapsed]: isParentCollapsed,
                      })}>
                      {editFormIndex === i ? (
                        <EditOperation
                          operation={restoreInvalidOperation(operation)}
                          exploration={exploration}
                          state={pipelineState}
                          models={models}
                          metrics={metrics}
                          setOperation={(operation) => setParentPipelineOperation(operation, i)}
                          onClose={closeEditForm}
                          onRemove={(event) =>
                            !isParentCollapsed && handleRemoveParentPipelineOperation(event, i)
                          }
                          className={styles.panel}
                          variables={variableDefinitions}
                        />
                      ) : (
                        <SavedOperation
                          icon={getOperationIcon(operation)}
                          title={getOperationTitle(operation, pipelineState, pipelineTitleById)}
                          description={getOperationOverview(operation, {
                            fields: flattenFieldGroups(
                              getFieldsWithRelatedFields(pipelineState, models),
                            ),
                            model: pipelineState.model,
                            variables,
                            timezone,
                          })}
                          onRemove={
                            removeEnabled
                              ? (event) => handleRemoveParentPipelineOperation(event, i)
                              : undefined
                          }
                          onClick={editEnabled ? () => openEditForm({ index: i }) : undefined}
                          disabled={operation.disabled}
                          onChangeDisabled={
                            operation.operation === 'filter'
                              ? (event, disabled) => {
                                  handleDisableParentPipelineOperation(event, i, disabled);
                                }
                              : undefined
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : firstOperation?.operation === 'sql' ? (
              <PipelineHeader
                icon="Database"
                title="SQL Query"
                description={
                  <span className={styles.expression}>
                    {firstOperation.parameters.sql === ''
                      ? 'Empty query'
                      : firstOperation.parameters.sql.slice(0, 50)}
                  </span>
                }>
                {(firstOperation.parameters.fields?.length ?? 0) > 0 && (
                  <IconButton
                    className={styles.combineBtn}
                    icon="Combine"
                    title="Combine"
                    onClick={props.onCombine}
                  />
                )}
              </PipelineHeader>
            ) : (
              <Tooltip
                placement="left"
                content={
                  <div>
                    <strong>{baseModel.name}</strong>
                    {baseModel.description !== null && <p>{baseModel.description}</p>}
                    {formattedLabels !== null && (
                      <>
                        <br />
                        <div>{formattedLabels}</div>
                      </>
                    )}
                  </div>
                }>
                <div>
                  <PipelineHeader
                    icon="Model"
                    title="Data model"
                    description={baseModel.name}
                    onClick={() => setPipelineSearchVisibility(true)}>
                    <IconButton
                      className={styles.combineBtn}
                      icon="Combine"
                      title="Combine"
                      onClick={props.onCombine}
                    />
                  </PipelineHeader>
                </div>
              </Tooltip>
            )}
          </>
        )}

        {pipeline.operations.map((operation, i) => {
          const unwrappedOperation = restoreInvalidOperation(operation);

          if (unwrappedOperation.operation === 'sql') {
            return <Fragment key={i} />;
          }

          if (unwrappedOperation.operation === 'joinPipeline') {
            return (
              <Fragment key={i}>
                <CombineEditor
                  operation={unwrappedOperation}
                  setExploration={props.setExploration}
                />
              </Fragment>
            );
          }

          const operationIdx = parentPipeline !== null ? parentPipeline.operations.length + i : i;
          const pipelineState = sortFields(
            getPipelineStateAtIndexOrThrow(dereferencedPipeline, operationIdx, stateContext),
            props.recordType,
            props.grouping,

            getCellViewOption('fieldOrder'),
          );

          return (
            <Fragment key={i}>
              <Separator
                addable={addFormData?.index !== i}
                onAdd={() => openAddMenu({ index: i })}
              />
              {addFormData?.index === i && (
                <>
                  <AddOperation
                    formData={addFormData}
                    state={pipelineState}
                    models={models}
                    metrics={metrics}
                    exploration={exploration}
                    onAdd={(operation: PipelineOperation) =>
                      handleAddOperation({
                        operation,
                        position: i,
                      })
                    }
                    onClose={closeAddForm}
                    onOpenMenu={() => openAddMenu({ index: i })}
                    onOpenForm={(formId: FormId) => openAddForm({ index: i, formId })}
                    variables={variableDefinitions}
                  />
                  <Separator
                    addable={addFormData?.index !== i}
                    onAdd={() => openAddMenu({ index: i })}
                  />
                </>
              )}
              {editFormIndex === operationIdx ? (
                <EditOperation
                  operation={unwrappedOperation}
                  exploration={exploration}
                  state={pipelineState}
                  models={models}
                  metrics={metrics}
                  setOperation={(operation) => setMainPipelineOperation(operation, i)}
                  onClose={closeEditForm}
                  onDuplicate={(operation) => handleAddPrefilledOperation(i, operation)}
                  onRemove={(event) => handleRemoveOperation(event, i)}
                  variables={variableDefinitions}
                />
              ) : (
                <SortableItemSimple
                  scope={'operation'}
                  index={i}
                  draggedIndex={draggedIndex}
                  setDraggedIndex={setDraggedIndex}
                  onReorder={handleReorder}>
                  <Operation
                    icon={getOperationIcon(operation)}
                    title={getOperationTitle(operation, pipelineState, pipelineTitleById)}
                    description={getOperationOverview(operation, {
                      fields: flattenFieldGroups(getFieldsWithRelatedFields(pipelineState, models)),
                      model: pipelineState.model,
                      variables,
                      timezone,
                    })}
                    onRemove={(event) => handleRemoveOperation(event, i)}
                    onClick={() => openEditForm({ index: operationIdx })}
                    disabled={operation.disabled}
                    invalid={!isValidOperation(operation)}
                    onChangeDisabled={
                      operation.operation === 'filter'
                        ? (event, disabled) => {
                            handleDisableOperation(event, i, disabled);
                          }
                        : undefined
                    }
                  />
                </SortableItemSimple>
              )}
            </Fragment>
          );
        })}
        {(showAddOperation || showAddButton) && (
          <Separator addable={false} onAdd={() => openAddMenu({ index: -1 })} />
        )}
        <div className={styles.addOperations}>
          {showAddOperation && (
            <AddOperation
              formData={addFormData}
              state={sortFields(
                getFinalStateOrThrow(dereferencedPipeline, stateContext),
                props.recordType,
                props.grouping,
                getCellViewOption('fieldOrder'),
              )}
              models={models}
              metrics={metrics}
              exploration={exploration}
              onAdd={(operation) => handleAddOperation({ operation, position: Infinity })}
              onClose={closeAddForm}
              onOpenMenu={() => openAddMenu({ index: -1 })}
              onOpenForm={(formId) => openAddForm({ index: -1, formId })}
              variables={variableDefinitions}
            />
          )}
          {showAddButton && (
            <Button
              variant={editFormIndex === null ? 'primary' : 'outlined'}
              icon={<Icon name="PlusCircle" size={16} />}
              onClick={() => openAddMenu({ index: -1 })}>
              Add
            </Button>
          )}
        </div>
      </div>
      {isPipelineSearchOpen ? (
        <BasePipelineSearchModal
          exploration={exploration}
          ignoredPipelineIds={
            selectedCell !== null &&
            isCellWithPipeline(selectedCell) &&
            selectedCell.pipeline.pipelineId !== undefined
              ? [selectedCell.pipeline.pipelineId, ...childPipelineIds]
              : undefined
          }
          onSelect={handleBasePipelineChange}
          onClose={() => setPipelineSearchVisibility(false)}
        />
      ) : null}
    </>
  );
};
